import React, { useState, useMemo } from 'react';
import { 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  TextField,
  useMediaQuery,
  CircularProgress,
  Collapse,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import DetailHeader from './components/DetailHeader.js';
import { useFirebase } from './contexts/FirebaseContext.js';
import { doc, collection, setDoc } from 'firebase/firestore';
import { db } from './firebase.js';

function CocktailMenuDetail({ menus, recipes, inventory, onAddRecipe, onRemoveRecipe, onDeleteMenu, onRenameMenu }) {
  const { menuId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [loading, setLoading] = useState(true);
  const [ingredientsExpanded, setIngredientsExpanded] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const { user } = useFirebase();
  const [isCreateSetDialogOpen, setIsCreateSetDialogOpen] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const menu = useMemo(() => {
    const foundMenu = menus.find(m => m.id === menuId);
    console.log('Menu from props:', foundMenu);
    return foundMenu ? { ...foundMenu, recipes: Array.isArray(foundMenu.recipes) ? foundMenu.recipes : [] } : null;
  }, [menus, menuId]);

  useMemo(() => {
    if (menu !== null) {
      setLoading(false);
    }
  }, [menu]);

  const menuRecipes = useMemo(() => {
    if (!menu?.recipes) return [];
    return menu.recipes.map(recipeId => {
      if (typeof recipeId === 'string' && recipeId.includes('/')) {
        const parts = recipeId.split('/');
        return parts[parts.length - 1];
      }
      return String(recipeId);
    });
  }, [menu?.recipes]);

  const availableRecipes = recipes.filter(recipe => !menuRecipes.includes(recipe.id));

  // Check for duplicate names across all recipes
  const nameCounts = useMemo(() => {
    const counts = {};
    recipes.forEach(recipe => {
      counts[recipe.name] = (counts[recipe.name] || 0) + 1;
    });
    return counts;
  }, [recipes]);

  useMemo(() => {
    console.log('Menu recipes IDs (normalized):', menuRecipes);
    console.log('Available recipes count:', availableRecipes.length);
    console.log('Recipes prop sample:', recipes.slice(0, 5));
    console.log('Rendered recipes:', menuRecipes.map(id => {
      const found = recipes.find(r => r.id === id);
      return { id, found: !!found, recipe: found || null };
    }));
  }, [menuRecipes, recipes, availableRecipes]);

  const ingredientsSummary = useMemo(() => {
    if (!menu) return {};
    
    const summary = new Map();
    
    menuRecipes.forEach(recipeId => {
      const recipe = recipes.find(r => r.id === recipeId);
      if (!recipe) {
        console.log(`Recipe ${recipeId} not found in recipes array for ingredients summary`);
        return;
      }

      recipe.ingredients.forEach(ing => {
        const ingredient = inventory.find(i => i.id === ing.ingredientId);
        if (!ingredient) return;

        if (!summary.has(ing.ingredientId)) {
          summary.set(ing.ingredientId, {
            id: ing.ingredientId,
            name: ingredient.name,
            type: ingredient.type,
            possessed: ingredient.possessed,
            recipes: new Set()
          });
        }
        
        summary.get(ing.ingredientId).recipes.add(recipe.name);
      });
    });

    const grouped = Array.from(summary.values()).reduce((acc, ing) => {
      acc[ing.type] = acc[ing.type] || [];
      acc[ing.type].push({
        ...ing,
        recipes: Array.from(ing.recipes)
      });
      return acc;
    }, {});

    return grouped;
  }, [menu, menuRecipes, recipes, inventory]);

  const totalIngredients = useMemo(() => {
    const count = Object.values(ingredientsSummary)
      .flatMap(ingredients => ingredients)
      .length;
    setIngredientsExpanded(count <= 10);
    return count;
  }, [ingredientsSummary]);

  const handleRename = () => {
    if (newMenuName.trim() && newMenuName !== menu.name) {
      onRenameMenu(menu.id, newMenuName.trim());
      setIsRenameDialogOpen(false);
    }
  };

  const handleRenameKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleRename();
    }
  };

  const openRenameDialog = () => {
    setNewMenuName(menu?.name || '');
    setIsRenameDialogOpen(true);
  };

  const headerActions = (
    <>
      <IconButton 
        onClick={() => setIsAddDialogOpen(true)}
        color="primary"
        size={isMobile ? "medium" : "small"}
      >
        <Icons.Add />
      </IconButton>
      <IconButton 
        onClick={() => setIsDeleteDialogOpen(true)}
        color="error"
        size={isMobile ? "medium" : "small"}
      >
        <Icons.Delete />
      </IconButton>
    </>
  );

  const handleDeleteMenu = async () => {
    await onDeleteMenu(menu.id);
    navigate('/cocktail-menus');
  };

  // Create ingredient set from menu
  const createIngredientSetFromMenu = async () => {
    if (!menu || !newSetName.trim() || !user) return;
    
    try {
      // Extract all unique ingredient IDs from menu recipes
      const ingredientIds = new Set();
      
      menuRecipes.forEach(recipeId => {
        const recipe = recipes.find(r => r.id === recipeId);
        if (recipe) {
          recipe.ingredients.forEach(ing => {
            if (ing.ingredientId) {
              ingredientIds.add(ing.ingredientId);
            }
          });
        }
      });
      
      // Create new ingredient set
      const setsRef = collection(db, `users/${user.uid}/ingredientSets`);
      const newSetRef = doc(setsRef);
      
      const newSet = {
        name: newSetName.trim(),
        ingredients: Array.from(ingredientIds),
        created: new Date()
      };
      
      await setDoc(newSetRef, newSet);
      
      // Show success message
      setSnackbarMessage(`Ingredient set "${newSetName}" created successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reset UI state
      setNewSetName('');
      setIsCreateSetDialogOpen(false);
    } catch (error) {
      console.error('Error creating ingredient set from menu:', error);
      setSnackbarMessage('Failed to create ingredient set');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Add Create Set button to the ingredients section
  const ingredientsHeader = (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      mb: { xs: 1, sm: 0.5 }
    }}>
      <Typography 
        variant="h6" 
        sx={{ 
          color: 'text.secondary',
          fontSize: { xs: '1.1rem', sm: '1rem' },
        }}
      >
        Required Ingredients ({totalIngredients})
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          startIcon={<Icons.PlaylistAdd />}
          onClick={() => {
            setNewSetName(menu?.name ? `${menu.name} Ingredients` : '');
            setIsCreateSetDialogOpen(true);
          }}
          sx={{ mr: 1 }}
          disabled={totalIngredients === 0}
        >
          Create Ingredient Set
        </Button>
        <IconButton
          onClick={() => setIngredientsExpanded(!ingredientsExpanded)}
          size={isMobile ? "medium" : "small"}
        >
          {ingredientsExpanded ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      minHeight: '100%',
      p: { xs: 2, sm: 3 }
    }}>
      <DetailHeader
        title={
          <Box 
            onClick={openRenameDialog}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                '& .editIcon': {
                  opacity: 1
                }
              }
            }}
          >
            {menu?.name || 'Unnamed Menu'}
            <Icons.Edit 
              className="editIcon"
              sx={{ 
                opacity: 0,
                transition: 'opacity 0.2s',
                fontSize: '0.8em'
              }} 
            />
          </Box>
        }
        actions={headerActions}
        backLink="/cocktail-menus"
        backLabel="Cocktail Menus"
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : !menu ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography>Menu not found</Typography>
        </Box>
      ) : menuRecipes.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 8,
            textAlign: 'center',
          }}
        >
          <Icons.LocalBar 
            sx={{ 
              fontSize: { xs: 64, sm: 48 },
              color: 'text.secondary',
              mb: 1,
              opacity: 0.7
            }}
          />
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 'normal' }}
          >
            This menu is empty
          </Typography>
          <Button
            variant="contained"
            startIcon={<Icons.Add />}
            onClick={() => setIsAddDialogOpen(true)}
            size={isMobile ? "large" : "medium"}
          >
            Add Your First Recipe
          </Button>
        </Box>
      ) : (
        <>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2,
              color: 'text.secondary',
              fontSize: { xs: '1.1rem', sm: '1rem' }
            }}
          >
            Recipes ({menuRecipes.length})
          </Typography>

          {menuRecipes.length > 0 ? (
            <List sx={{ 
              padding: 0,
              backgroundColor: theme.palette.background.paper,
              borderRadius: { xs: 2, sm: 1 },
              overflow: 'hidden',
            }}>
              {menuRecipes.map(recipeId => {
                const recipe = recipes.find(r => r.id === recipeId);
                if (!recipe) {
                  console.log(`Recipe ${recipeId} not found in recipes array`);
                  return null;
                }
                const displayName = nameCounts[recipe.name] > 1 ? `${recipe.name} (${recipe.origin})` : recipe.name;
                
                return (
                  <ListItem
                    key={recipe.id}
                    component={Link}
                    to={`/recipe/${recipe.id}`}
                    state={{ from: { type: 'menu', id: menu.id } }}
                    sx={{
                      borderRadius: { xs: 2, sm: 1 },
                      mb: 1.5,
                      position: 'relative',
                      backgroundColor: theme.palette.background.paper,
                      transition: 'background-color 0.15s ease-out',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        textDecoration: 'none',
                      },
                      '&:active': {
                        backgroundColor: theme.palette.action.selected,
                      },
                      py: { xs: 1, sm: 0.5 },
                      boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.1)', sm: 'none' },
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText 
                      primary={displayName}
                      primaryTypographyProps={{
                        sx: { 
                          fontSize: { xs: '1rem', sm: 'inherit' },
                          fontWeight: { xs: 500, sm: 400 }
                        }
                      }}
                      secondary={recipe.ingredients.map(ing => {
                        const ingredient = inventory.find(i => i.id === ing.ingredientId);
                        return ingredient ? ingredient.name : ing.ingredientId;
                      }).join(", ")}
                      secondaryTypographyProps={{
                        sx: { 
                          color: theme.palette.text.secondary,
                          fontSize: { xs: '0.875rem', sm: '0.75rem' },
                          mt: { xs: 0.5, sm: 0 }
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton 
                      edge="end" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemoveRecipe(menu.id, recipe.id);
                      }}
                      size={isMobile ? "medium" : "small"}
                      sx={{ 
                        ml: 1,
                        color: 'error.light'
                      }}
                    >
                      <Icons.Remove />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', backgroundColor: theme.palette.background.paper, borderRadius: { xs: 2, sm: 1 } }}>
              <Typography color="text.secondary">
                No recipes found for this menu.
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            {ingredientsHeader}
            <Collapse in={ingredientsExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: { xs: 1.5, sm: 1 }
              }}>
                {Object.keys(ingredientsSummary).length > 0 ? (
                  Object.values(ingredientsSummary)
                    .flatMap(ingredients => ingredients)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(ing => (
                      <Chip
                        key={ing.id}
                        label={`${ing.name} (${ing.recipes.length})`}
                        color={ing.possessed ? "default" : "error"}
                        size={isMobile ? "medium" : "small"}
                        variant={ing.possessed ? "outlined" : "filled"}
                        sx={{ 
                          height: { xs: 'auto', sm: 32 },
                          py: { xs: 0.75, sm: 0 },
                          '& .MuiChip-label': {
                            px: { xs: 1.5, sm: 1 },
                            whiteSpace: 'normal',
                          }
                        }}
                        component={Link}
                        to={`/ingredient/${ing.id}`}
                        clickable
                      />
                    ))
                ) : (
                  <Typography color="text.secondary">
                    No ingredients required.
                  </Typography>
                )}
              </Box>
            </Collapse>
            {!ingredientsExpanded && Object.keys(ingredientsSummary).length > 0 && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mt: 0.5, fontSize: { xs: '0.875rem', sm: '0.75rem' } }}
              >
                {totalIngredients} ingredients
              </Typography>
            )}
          </Box>
        </>
      )}

      <Dialog 
        open={isAddDialogOpen} 
        onClose={() => {
          setIsAddDialogOpen(false);
          setSelectedRecipes([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            width: { xs: '95%', sm: '100%' }, 
            mx: 'auto',
            maxHeight: { xs: '80vh', sm: '65vh' },
            borderRadius: { xs: 2, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 1.5 }
        }}>
          Add Recipes to Menu
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ py: 0 }}>
            {availableRecipes.length > 0 ? (
              availableRecipes.map(recipe => {
                const displayName = nameCounts[recipe.name] > 1 ? `${recipe.name} (${recipe.origin})` : recipe.name;
                return (
                  <ListItem
                    key={recipe.id}
                    sx={{ 
                      py: { xs: 1.5, sm: 1 },
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRecipes.includes(recipe.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRecipes(prev => [...prev, recipe.id]);
                            } else {
                              setSelectedRecipes(prev => prev.filter(id => id !== recipe.id));
                            }
                          }}
                          size={isMobile ? "medium" : "small"}
                        />
                      }
                      label={displayName} // Conditional Name (Origin)
                      sx={{ 
                        width: '100%', 
                        m: 0,
                        '& .MuiFormControlLabel-label': { 
                          fontSize: { xs: '1rem', sm: 'inherit' }
                        }
                      }}
                    />
                  </ListItem>
                );
              })
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  All recipes are already in this menu
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 } }}>
          <Button 
            onClick={() => {
              setIsAddDialogOpen(false);
              setSelectedRecipes([]);
            }}
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              selectedRecipes.forEach(recipeId => onAddRecipe(menu.id, recipeId));
              setIsAddDialogOpen(false);
              setSelectedRecipes([]);
            }}
            color="primary"
            variant="contained"
            disabled={selectedRecipes.length === 0}
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Add{selectedRecipes.length > 0 ? ` (${selectedRecipes.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        PaperProps={{
          sx: { 
            width: { xs: '95%', sm: '100%' }, 
            mx: 'auto',
            borderRadius: { xs: 2, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 1.5 }
        }}>
          Delete Menu
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography>
            Are you sure you want to delete "{menu?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 } }}>
          <Button 
            onClick={() => setIsDeleteDialogOpen(false)}
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteMenu} 
            color="error"
            variant="contained"
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRenameDialogOpen}
        onClose={() => setIsRenameDialogOpen(false)}
        PaperProps={{
          sx: { 
            width: { xs: '95%', sm: '100%' }, 
            mx: 'auto',
            borderRadius: { xs: 2, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 1.5 }
        }}>
          Rename Menu
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Menu Name"
            fullWidth
            value={newMenuName}
            onChange={(e) => setNewMenuName(e.target.value)}
            onKeyPress={handleRenameKeyPress}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: { xs: 2, sm: 1 },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 } }}>
          <Button 
            onClick={() => setIsRenameDialogOpen(false)}
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRename} 
            color="primary"
            variant="contained"
            sx={{ minWidth: { xs: '80px', sm: '64px' } }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Ingredient Set Dialog */}
      <Dialog open={isCreateSetDialogOpen} onClose={() => setIsCreateSetDialogOpen(false)}>
        <DialogTitle>Create Ingredient Set</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Create a new ingredient set containing all ingredients from this menu.
            This will help you track which drinks you can make with these ingredients.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Set Name"
            fullWidth
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateSetDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={createIngredientSetFromMenu}
            color="primary"
            disabled={!newSetName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: { xs: 4, sm: 1 }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CocktailMenuDetail;